import { CloseOutlined } from '@ant-design/icons';
import { Dispatch, memo, SetStateAction, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/Button';
import Form from '../../../components/Form';
import Input from '../../../components/FormInput/Input';
import getErrors from '../../../helpers/errors';
import { ShowFormMessage } from '../../../helpers/message';
import { MAP } from '../../../navigation/constants';
import {
  IFilters,
  PotentialAudienceProps,
  usePostSavePotentialAudienceMutation,
  useUpdatePotentialAudienceMutation,
} from '../../../services/map/potentialAudience';
import { Body, H4 } from '../../../styles/fonts';
import {
  FormItem,
  ModalContainer,
  ModalContent,
  ModalPatternBar,
  ModalSave,
  SaveForm,
} from './styles';

interface IProps {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  filters?: IFilters;
  totalResults?: number;
}

const ModalSavePotentialAudience = ({
  visible,
  setVisible,
  filters,
  totalResults = 0,
}: IProps) => {
  const [form] = Form.useForm();

  const { id } = useParams();
  const { pathname } = useLocation();

  const [postSavePotentialAudience, configSavePotentialAudience] =
    usePostSavePotentialAudienceMutation();

  const [updatePotentialAudience, configUpdatePotentialAudience] =
    useUpdatePotentialAudienceMutation();

  const navigate = useNavigate();

  const handleSubmit = async (data: PotentialAudienceProps) => {
    if (filters) {
      const newObj = { ...filters };
      newObj.name = data.name;
      newObj.limitSearchQuery = data.quantity;
      delete newObj.quantity;

      if (!id) {
        await postSavePotentialAudience(newObj);
      } else {
        if (pathname.includes('segmentation-analysis')) {
          await postSavePotentialAudience(newObj);
        }
        await updatePotentialAudience({ id, ...newObj });
      }
    }
  };

  const showMessagePostSavePotentialAudience = useCallback(() => {
    const { isSuccess, isError, error } = configSavePotentialAudience;

    let errors: any;

    if (error) {
      const { data: errorData }: any = error;
      errors = errorData;
    }
    ShowFormMessage.SuccessOrError({
      status: {
        isSuccess,
        isError,
      },
      config: {
        successMessage: 'Público potencial adicionado com sucesso.',
        errorMessage: error ? getErrors(errors.errors) : '',
      },
    });
    if (isSuccess) {
      setVisible(false);
      form.resetFields();
    }
  }, [configSavePotentialAudience, form, setVisible]);

  useEffect(showMessagePostSavePotentialAudience, [
    showMessagePostSavePotentialAudience,
  ]);

  const showMessageUpdatePotentialAudience = useCallback(() => {
    const { isSuccess, isError, error } = configUpdatePotentialAudience;

    let errors: any;

    if (error) {
      const { data: errorData }: any = error;
      errors = errorData;
    }
    ShowFormMessage.SuccessOrError({
      status: {
        isSuccess,
        isError,
      },
      config: {
        successMessage: 'Público potencial editado com sucesso.',
        errorMessage: error ? getErrors(errors.errors) : '',
      },
    });
    if (isSuccess) {
      setVisible(false);
      form.resetFields();
    }
  }, [configUpdatePotentialAudience, form, setVisible]);

  useEffect(showMessageUpdatePotentialAudience, [
    showMessageUpdatePotentialAudience,
  ]);

  useEffect(() => {
    const { isSuccess: saveSuccess } = configSavePotentialAudience;
    const { isSuccess: updateSuccess } = configUpdatePotentialAudience;

    if (saveSuccess || updateSuccess) {
      navigate(MAP);
    }
  }, [configSavePotentialAudience, configUpdatePotentialAudience]);

  useEffect(() => {
    if (visible && totalResults > 0) {
      form.setFieldsValue({ quantity: totalResults });
    }
  }, [visible, totalResults, form]);

  return (
    <ModalSave
      visible={visible}
      title={null}
      footer={null}
      closable={false}
      width="35rem"
    >
      <ModalContainer>
        <ModalPatternBar />
        <ModalContent>
          <SaveForm
            form={form}
            layout="vertical"
            onFinish={data => handleSubmit(data)}
            initialValues={{ name: filters?.name }}
          >
            <header>
              <H4>Salvar público potencial</H4>
              <CloseOutlined onClick={() => setVisible(!visible)} />
            </header>
            <main>
              <FormItem label={<Body>Nome</Body>} name="name">
                <Input size="large" style={{ width: '100%' }} />
              </FormItem>
              <FormItem
                label={<Body>Defina a quantidade do seu público</Body>}
                name="quantity"
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        value === undefined ||
                        value === null ||
                        value === ''
                      ) {
                        return Promise.reject(
                          new Error('Por favor, insira a quantidade'),
                        );
                      }
                      if (value <= 0) {
                        return Promise.reject(
                          new Error('A quantidade deve ser maior que 0'),
                        );
                      }
                      if (totalResults && value > totalResults) {
                        return Promise.reject(
                          new Error(
                            `A quantidade não pode ser maior que ${totalResults}`,
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                  max={totalResults}
                />
              </FormItem>
            </main>
            <footer>
              <Button onClick={() => setVisible(!visible)}>Cancelar</Button>
              <Button type="primary" htmlType="submit">
                Salvar e finalizar
              </Button>
            </footer>
          </SaveForm>
        </ModalContent>
      </ModalContainer>
    </ModalSave>
  );
};

export default memo(ModalSavePotentialAudience);
